import { skipToken } from '@apollo/client'
import { differenceInDays, fromUnixTime } from 'date-fns'
import { TriangleAlert } from 'lucide-react'
import { Suspense, use, useMemo, useState } from 'react'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { ManageBillingButton } from '@/components/shared/manage-billing-button'
import { P } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { ACCOUNT_DISABLED_AFTER_DAYS } from '@/config/billing'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import type {
  PastDueInvoiceQuery,
  PastDueInvoiceQueryVariables,
} from '@/components/layout/past-due-invoice-notice/index.types.gen'

const PAST_DUE_INVOICE_QUERY = gql`
  query PastDueInvoice($idCompany: String!) {
    company(id: $idCompany) {
      id
      sevallaBilling {
        id
        pastDueInvoices {
          id
          amountDue
          dueDate
        }
      }
    }
  }
`

const PastDueInvoiceNoticeContent = () => {
  const { isSuspended, suspensionReason } = use(CompanySuspensionContext)
  const { idCompany } = useIdCompany()
  const [isOpen, setOpen] = useState(false)
  const { data } = useSuspenseQuery<
    PastDueInvoiceQuery,
    PastDueInvoiceQueryVariables
  >(
    PAST_DUE_INVOICE_QUERY,
    !idCompany ? skipToken : { variables: { idCompany } }
  )

  const pastDueInvoices = useMemo(
    () => data?.company?.sevallaBilling?.pastDueInvoices || [],
    [data]
  )
  const hasPastDueInvoices = (pastDueInvoices.length ?? 0) > 0

  const onClose = () => {
    setOpen(false)
  }

  const earliestDueDate = useMemo(() => {
    if (pastDueInvoices.every((invoice) => !invoice.dueDate)) {
      return null
    }
    const earliest = pastDueInvoices.reduce((earliest, invoice) => {
      const dueDate = new Date(invoice.dueDate)
      if (dueDate < earliest) {
        return dueDate
      }
      return earliest
    }, new Date())
    return earliest
  }, [pastDueInvoices])

  const daysPassedSinceDue = earliestDueDate
    ? differenceInDays(
        new Date(),
        fromUnixTime(earliestDueDate.getTime() / 1000)
      )
    : 0

  const accountDisabledInDays: number = Math.max(
    0,
    ACCOUNT_DISABLED_AFTER_DAYS - daysPassedSinceDue
  )

  if (!hasPastDueInvoices) {
    return null
  }

  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        <TriangleAlert />
        <span>
          {pastDueInvoices.length > 1
            ? `${pastDueInvoices.length} Past due invoices`
            : 'Past due invoice'}
        </span>
      </Button>
      <Separator orientation="vertical" className="h-4!" />
      <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent
          className="sm:max-w-[500px] overflow-hidden"
          onEscapeKeyDown={() => onClose()}
        >
          <SheetHeader className="pb-0">
            <SheetTitle className="text-destructive">Unpaid invoice</SheetTitle>
          </SheetHeader>
          <div className="px-4">
            <P className="mt-0">
              {`You have ${
                pastDueInvoices.length > 1
                  ? `unpaid ${pastDueInvoices.length} invoices`
                  : 'an unpaid invoice'
              }. ${
                daysPassedSinceDue > 0
                  ? `The ${pastDueInvoices.length > 1 ? 'earliest ' : ''}invoice is
              overdue by ${daysPassedSinceDue}
              ${daysPassedSinceDue > 1 ? 'days' : 'day'}.`
                  : ''
              }`}
            </P>
            <P>
              {isSuspended && suspensionReason === 'payment' ? (
                <b>
                  Your account is currently suspended due to unpaid invoices.
                  Please settle the outstanding balance to restore your
                  services.
                </b>
              ) : (
                <b>
                  Your account will be disabled in {accountDisabledInDays} days
                  if the unpaid{' '}
                  {pastDueInvoices.length > 1 ? 'invoices' : 'invoice'} remains
                  unpaid.
                </b>
              )}
            </P>
            <P>
              Please update your payment method through our billing portal to
              avoid service interruptions!
            </P>
          </div>
          <SheetFooter className="sticky">
            <ManageBillingButton variant="destructive" />
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}

export const PastDueInvoiceNotice = () => {
  return (
    <Suspense fallback={null}>
      <PastDueInvoiceNoticeContent />
    </Suspense>
  )
}
